import { downloadBlob } from "./download-blob";

export type FileWithPath = {
  file: File;
  zipPath: string;
};

// jszip is a heavyweight dep; load it only when the user triggers a download.
async function loadJSZip() {
  const mod = await import("jszip");
  return mod.default;
}

/**
 * Bundles the given files into a ZIP archive and triggers a browser download.
 * Disambiguates colliding zip paths by appending a numeric suffix to the
 * basename (e.g. `model.obj` -> `model_2.obj`).
 */
export async function buildAndDownloadZip(files: FileWithPath[], zipName: string): Promise<void> {
  const JSZip = await loadJSZip();
  const zip = new JSZip();
  const usedPaths = new Map<string, number>();
  const usedFinalPaths = new Set<string>();

  files.forEach(({ file, zipPath }) => {
    const basePath = zipPath;
    let count = usedPaths.get(basePath) ?? 1;
    let finalPath = basePath;

    while (usedFinalPaths.has(finalPath)) {
      count += 1;
      const lastDotIndex = basePath.lastIndexOf(".");
      if (lastDotIndex > 0) {
        const name = basePath.slice(0, lastDotIndex);
        const extension = basePath.slice(lastDotIndex);
        finalPath = `${name}_${count}${extension}`;
      } else {
        finalPath = `${basePath}_${count}`;
      }
    }
    usedPaths.set(basePath, count);
    usedFinalPaths.add(finalPath);

    zip.file(finalPath, file);
  });

  const content = await zip.generateAsync({ type: "blob" });
  await downloadBlob(content, zipName);
}
